import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { rootUrl } from '../../config/config';
import { FaSearch, FaFilter, FaEye, FaExclamationCircle, FaCheckCircle, FaClock, FaChevronRight } from 'react-icons/fa';

const EmailLogs = () => {
    const { token } = useAuth();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);

    // Filters
    const [emailType, setEmailType] = useState('');
    const [sendStatus, setSendStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Fetch email logs
    const fetchEmailLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page,
                limit,
                ...(emailType && { email_type: emailType }),
                ...(sendStatus && { send_status: sendStatus }),
                ...(searchTerm && { search: searchTerm }),
                ...(dateFrom && { date_from: dateFrom }),
                ...(dateTo && { date_to: dateTo })
            });

            const response = await axios.get(`${rootUrl}/api/email-logs?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLogs(response.data.data);
                setTotalPages(response.data.pagination.pages);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load email logs');
        } finally {
            setLoading(false);
        }
    };

    // Fetch email statistics
    const fetchEmailStats = async () => {
        try {
            const response = await axios.get(`${rootUrl}/api/email-logs/stats/summary`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (err) {
            console.error('Failed to load email statistics:', err);
        }
    };

    // Fetch logs and stats on mount and when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchEmailLogs();
    }, [page, limit, emailType, sendStatus, searchTerm, dateFrom, dateTo]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchEmailStats();
    }, []);

    // View log details
    const handleViewLog = async (logId) => {
        try {
            const response = await axios.get(`${rootUrl}/api/email-logs/${logId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setSelectedLog(response.data.data);
                setShowModal(true);
            }
        } catch (err) {
            setError('Failed to load email details');
        }
    };

    // Get status badge color
    const getStatusColor = (status) => {
        switch (status) {
            case 'SENT':
                return '#10b981';
            case 'FAILED':
                return '#ef4444';
            case 'BOUNCED':
                return '#f97316';
            case 'OPENED':
                return '#3b82f6';
            default:
                return '#6b7280';
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'SENT':
                return <FaCheckCircle style={{ color: '#10b981', fontSize: '18px' }} />;
            case 'FAILED':
                return <FaExclamationCircle style={{ color: '#ef4444', fontSize: '18px' }} />;
            case 'BOUNCED':
                return <FaExclamationCircle style={{ color: '#f97316', fontSize: '18px' }} />;
            case 'OPENED':
                return <FaEye style={{ color: '#3b82f6', fontSize: '18px' }} />;
            default:
                return <FaClock style={{ color: '#6b7280', fontSize: '18px' }} />;
        }
    };

    // Format date and time
    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    // Reset filters
    const handleResetFilters = () => {
        setEmailType('');
        setSendStatus('');
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
        setPage(1);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            {/* Header */}
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Email Logs</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Track and view all system emails
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {/* Statistics Cards */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <p className="text-gray-600 text-sm font-medium mb-2">Total Emails</p>
                            <p className="text-3xl font-bold text-[#2a2e30]">{stats.total_emails}</p>
                        </div>
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <p className="text-gray-600 text-sm font-medium mb-2">Successfully Sent</p>
                            <p className="text-3xl font-bold text-[#10b981]">{stats.sent_count}</p>
                            <p className="text-xs text-gray-500 mt-2">Success Rate: {stats.success_rate}%</p>
                        </div>
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <p className="text-gray-600 text-sm font-medium mb-2">Failed</p>
                            <p className="text-3xl font-bold text-[#ef4444]">{stats.failed_count}</p>
                        </div>
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <p className="text-gray-600 text-sm font-medium mb-2">Unique Recipients</p>
                            <p className="text-3xl font-bold text-[#3b82f6]">{stats.unique_recipients}</p>
                        </div>
                    </div>
                )}

                {/* Filters Section */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                    <div className="flex items-center mb-4">
                        <FaFilter style={{ color: '#FF5733', fontSize: '20px', marginRight: '8px' }} />
                        <h2 className="text-lg font-semibold text-[#2a2e30]">Filters & Search</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        {/* Search */}
                        <div className="relative">
                            <FaSearch style={{ position: 'absolute', left: '12px', top: '12px', color: '#9ca3af', fontSize: '18px' }} />
                            <input
                                type="text"
                                placeholder="Search subject, email, or ID..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF5733] text-sm"
                            />
                        </div>

                        {/* Email Type Filter */}
                        <select
                            value={emailType}
                            onChange={(e) => {
                                setEmailType(e.target.value);
                                setPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF5733] text-sm"
                        >
                            <option value="">All Email Types</option>
                            <option value="GENERAL">General</option>
                            <option value="TEMPLATE">Template</option>
                            <option value="NOTIFICATION">Notification</option>
                            <option value="ALERT">Alert</option>
                            <option value="BULK">Bulk</option>
                        </select>

                        {/* Status Filter */}
                        <select
                            value={sendStatus}
                            onChange={(e) => {
                                setSendStatus(e.target.value);
                                setPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF5733] text-sm"
                        >
                            <option value="">All Statuses</option>
                            <option value="SENT">Sent</option>
                            <option value="FAILED">Failed</option>
                            <option value="BOUNCED">Bounced</option>
                            <option value="OPENED">Opened</option>
                        </select>

                        {/* Date From */}
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => {
                                setDateFrom(e.target.value);
                                setPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF5733] text-sm"
                        />

                        {/* Date To */}
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => {
                                setDateTo(e.target.value);
                                setPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF5733] text-sm"
                        />

                        {/* Limit */}
                        <select
                            value={limit}
                            onChange={(e) => {
                                setLimit(parseInt(e.target.value));
                                setPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF5733] text-sm"
                        >
                            <option value="10">10 per page</option>
                            <option value="20">20 per page</option>
                            <option value="50">50 per page</option>
                            <option value="100">100 per page</option>
                        </select>
                    </div>

                    <button
                        onClick={handleResetFilters}
                        className="text-sm text-[#FF5733] hover:text-[#e64520] font-medium"
                    >
                        Reset Filters
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
                        <FaExclamationCircle style={{ color: '#ef5350', fontSize: '20px', marginRight: '12px' }} />
                        <span className="text-red-700">{error}</span>
                    </div>
                )}

                {/* Email Logs Table */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF5733]"></div>
                            <p className="mt-4 text-gray-600">Loading email logs...</p>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="p-8 text-center">
                            <p className="text-gray-500">No email logs found</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-3 text-left font-semibold text-gray-700">Status</th>
                                            <th className="px-6 py-3 text-left font-semibold text-gray-700">Subject</th>
                                            <th className="px-6 py-3 text-left font-semibold text-gray-700">To</th>
                                            <th className="px-6 py-3 text-left font-semibold text-gray-700">Type</th>
                                            <th className="px-6 py-3 text-left font-semibold text-gray-700">Date</th>
                                            <th className="px-6 py-3 text-center font-semibold text-gray-700">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map((log) => (
                                            <tr key={log.id} className="border-b border-gray-200 hover:bg-gray-50">
                                                <td className="px-6 py-3">
                                                    <div className="flex items-center">
                                                        {getStatusIcon(log.send_status)}
                                                        <span className="ml-2 text-xs font-medium" style={{ color: getStatusColor(log.send_status) }}>
                                                            {log.send_status}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3">
                                                    <p className="font-medium text-gray-900 truncate max-w-xs" title={log.subject}>
                                                        {log.subject}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-3">
                                                    <p className="text-gray-600 truncate max-w-xs" title={log.to_email}>
                                                        {log.to_email}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-3">
                                                    <span className="px-3 py-1 inline-block text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                                                        {log.email_type}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3 text-gray-600 text-xs">
                                                    {formatDateTime(log.input_date)}
                                                </td>
                                                <td className="px-6 py-3 text-center">
                                                    <button
                                                        onClick={() => handleViewLog(log.id)}
                                                        className="text-[#FF5733] hover:text-[#e64520] font-medium text-sm inline-flex items-center"
                                                    >
                                                        View
                                                        <FaChevronRight style={{ marginLeft: '4px', fontSize: '16px' }} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                <div className="text-sm text-gray-600">
                                    Page {page} of {totalPages}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setPage(Math.max(1, page - 1))}
                                        disabled={page === 1}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                                        disabled={page === totalPages}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            {showModal && selectedLog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-gray-50 border-b border-gray-200 p-6 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-[#2a2e30]">Email Details</h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Subject</label>
                                    <p className="text-gray-900 font-medium">{selectedLog.subject}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                                    <div className="flex items-center">
                                        {getStatusIcon(selectedLog.send_status)}
                                        <span className="ml-2 font-medium" style={{ color: getStatusColor(selectedLog.send_status) }}>
                                            {selectedLog.send_status}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
                                    <p className="text-gray-900">{selectedLog.from_name} &lt;{selectedLog.from_email}&gt;</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
                                    <p className="text-gray-900">{selectedLog.to_names}</p>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Email Type</label>
                                    <span className="inline-block px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                                        {selectedLog.email_type}
                                    </span>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Sent At</label>
                                    <p className="text-gray-900">{formatDateTime(selectedLog.sent_at || selectedLog.input_date)}</p>
                                </div>

                                {selectedLog.cc_recipients && (
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">CC</label>
                                        <p className="text-gray-900">{selectedLog.cc_recipients}</p>
                                    </div>
                                )}

                                {selectedLog.bcc_recipients && (
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">BCC</label>
                                        <p className="text-gray-900">{selectedLog.bcc_recipients}</p>
                                    </div>
                                )}

                                {selectedLog.custom_id && (
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Custom ID</label>
                                        <p className="text-gray-900 font-mono text-sm">{selectedLog.custom_id}</p>
                                    </div>
                                )}

                                {selectedLog.custom_campaign && (
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Campaign</label>
                                        <p className="text-gray-900">{selectedLog.custom_campaign}</p>
                                    </div>
                                )}

                                {selectedLog.mailjet_message_id && (
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Mailjet Message ID</label>
                                        <p className="text-gray-900 font-mono text-sm">{selectedLog.mailjet_message_id}</p>
                                    </div>
                                )}

                                {selectedLog.attachments_count > 0 && (
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Attachments</label>
                                        <p className="text-gray-900">{selectedLog.attachments_count} file(s)</p>
                                    </div>
                                )}

                                {selectedLog.error_message && (
                                    <div className="col-span-2 bg-red-50 border border-red-200 rounded p-3">
                                        <label className="block text-xs font-semibold text-red-700 mb-1">Error</label>
                                        <p className="text-red-700 text-sm">{selectedLog.error_message}</p>
                                    </div>
                                )}
                            </div>

                            {/* HTML Content Preview */}
                            {selectedLog.html_content && (
                                <div className="border-t border-gray-200 pt-4 mt-4">
                                    <label className="block text-sm font-semibold text-gray-600 mb-2">HTML Content Preview</label>
                                    <div className="bg-white rounded border border-gray-200 overflow-hidden" style={{ maxHeight: '400px' }}>
                                        <iframe
                                            srcDoc={selectedLog.html_content}
                                            title="Email Preview"
                                            className="w-full"
                                            style={{ height: '400px', border: 'none' }}
                                            sandbox="allow-same-origin"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 bg-[#FF5733] text-white rounded-lg font-medium hover:bg-[#e64520] transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailLogs;
